import type React from 'react';

export const BUTTON_BORDER_STYLE_OPTIONS = [
  { value: 'solid', label: 'Solid' },
  { value: 'dashed', label: 'Dashed' },
  { value: 'dotted', label: 'Dotted' },
  { value: 'none', label: 'None' },
] as const;

export type ButtonBorderStyle = (typeof BUTTON_BORDER_STYLE_OPTIONS)[number]['value'];

export type ButtonShadowValue = {
  x?: number;
  y?: number;
  blur?: number;
  spread?: number;
  color?: string;
};

export type ButtonTextShadowValue = {
  x?: number;
  y?: number;
  blur?: number;
  color?: string;
};

export type ButtonTextGlowValue = {
  blur?: number;
  color?: string;
  opacity?: number;
};

export type ButtonTextReliefValue = {
  enabled?: boolean;
  highlightColor?: string;
  shadowColor?: string;
  distance?: number;
  blur?: number;
  opacity?: number;
};

export type ButtonTextEffectToggles = {
  shadow?: boolean;
  outerGlow?: boolean;
  innerGlow?: boolean;
  emboss?: boolean;
  bevel?: boolean;
  pressed?: boolean;
};

export type ButtonTextEffectPreset = 'none' | 'emboss' | 'bevel' | 'pressed';
export type ButtonTextFillMode = 'solid' | 'hollow' | 'image' | 'gradient';
export type ButtonTextBlendMode =
  | 'normal'
  | 'multiply'
  | 'screen'
  | 'overlay'
  | 'soft-light'
  | 'difference'
  | 'exclusion';
export type ButtonImageFitMode = 'cover' | 'contain' | 'fill';
export type ButtonSizingMode = 'hug' | 'stretch';

export const BUTTON_VISUAL_HEIGHT_PRESETS = {
  sm: 32,
  md: 40,
  default: 48,
  lg: 56,
  xl: 64,
} as const;

export type ButtonVisualHeightPreset = keyof typeof BUTTON_VISUAL_HEIGHT_PRESETS;

export type ButtonBorderSideValue = {
  width?: number;
  color?: string;
  style?: ButtonBorderStyle;
};

export type ButtonBorderSides = {
  top?: ButtonBorderSideValue;
  right?: ButtonBorderSideValue;
  bottom?: ButtonBorderSideValue;
  left?: ButtonBorderSideValue;
};

export type ButtonVisualState = {
  backgroundColor?: string;
  backgroundOpacity?: number;
  borderColor?: string;
  borderWidth?: number;
  borderStyle?: ButtonBorderStyle;
  borderRadius?: number;
  borderLinked?: boolean;
  borderSides?: ButtonBorderSides;
  boxShadow?: ButtonShadowValue;
  textColor?: string;
  fontFamilyKey?: string;
  fontSize?: number;
  fontSizeMobile?: number;
  fontSizeDesktop?: number;
  fontWeight?: number;
  fontStyle?: 'normal' | 'italic';
  textDecorationLine?: 'none' | 'underline';
  textTransform?: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
  textAlign?: 'left' | 'center' | 'right';
  textRole?: 'body' | 'heading' | 'wordmark';
  textSizingMode?: 'frame' | 'scale';
  lineHeight?: number;
  letterSpacing?: number;
  wordSpacing?: number;
  textOpacity?: number;
  textBlendMode?: ButtonTextBlendMode;
  textShadow?: ButtonTextShadowValue;
  textFillMode?: ButtonTextFillMode;
  textFillImageUrl?: string;
  textFillImageScale?: number;
  textFillImagePositionX?: number;
  textFillImagePositionY?: number;
  textGradientStartColor?: string;
  textGradientEndColor?: string;
  textGradientAngle?: number;
  textStrokeColor?: string;
  textStrokeWidth?: number;
  textOuterGlow?: ButtonTextGlowValue;
  textInnerGlow?: ButtonTextGlowValue;
  textEffects?: ButtonTextEffectToggles;
  textEmboss?: ButtonTextReliefValue;
  textBevel?: ButtonTextReliefValue;
  textPressed?: ButtonTextReliefValue;
  textEffectPreset?: ButtonTextEffectPreset;
  imageFit?: ButtonImageFitMode;
  imageScale?: number;
  imageScaleX?: number;
  imageScaleY?: number;
  imageRotation?: number;
  imagePositionX?: number;
  imagePositionY?: number;
  imageFlipX?: boolean;
  imageFlipY?: boolean;
  sizingMode?: ButtonSizingMode;
  visualHeight?: number;
  alignX?: 'left' | 'center' | 'right';
  alignY?: 'top' | 'center' | 'bottom';
  paddingX?: number;
  paddingY?: number;
  marginX?: number;
  marginY?: number;
};

export type ButtonDesignStyle = ButtonVisualState & {
  hover?: ButtonVisualState;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asFiniteNumber(value: unknown): number | undefined {
  return Number.isFinite(value) ? Number(value) : undefined;
}

function clamp(value: number | undefined, min: number, max: number) {
  if (!Number.isFinite(value)) return undefined;
  return Math.max(min, Math.min(max, value as number));
}

function normalizeButtonSizingMode(value: unknown): ButtonSizingMode | undefined {
  if (value === 'hug' || value === 'fit') return 'hug';
  if (value === 'stretch' || value === 'fill') return 'stretch';
  return undefined;
}

function normalizeButtonVisualHeight(value: unknown): number | undefined {
  if (typeof value === 'string' && value in BUTTON_VISUAL_HEIGHT_PRESETS) {
    return BUTTON_VISUAL_HEIGHT_PRESETS[value as ButtonVisualHeightPreset];
  }
  return clamp(asFiniteNumber(value), 24, 96);
}

function normalizeButtonAlignX(value: unknown): ButtonVisualState['alignX'] {
  if (value === 'left' || value === 'center' || value === 'right') return value;
  if (value === 'stretch') return 'center';
  return undefined;
}

function normalizeButtonAlignY(value: unknown): ButtonVisualState['alignY'] {
  if (value === 'top' || value === 'center' || value === 'bottom') return value;
  if (value === 'stretch') return 'center';
  return undefined;
}

function normalizeBorderSide(value: unknown): ButtonBorderSideValue | undefined {
  const record = asRecord(value);
  if (!record) return undefined;
  const style =
    record.style === 'solid' ||
    record.style === 'dashed' ||
    record.style === 'dotted' ||
    record.style === 'none'
      ? record.style
      : undefined;
  return {
    width: clamp(asFiniteNumber(record.width), 0, 10),
    color: typeof record.color === 'string' ? record.color : undefined,
    style,
  };
}

function normalizeBorderSides(value: unknown): ButtonBorderSides | undefined {
  const record = asRecord(value);
  if (!record) return undefined;
  return {
    top: normalizeBorderSide(record.top),
    right: normalizeBorderSide(record.right),
    bottom: normalizeBorderSide(record.bottom),
    left: normalizeBorderSide(record.left),
  };
}

function normalizeShadow(value: unknown): ButtonShadowValue | undefined {
  const record = asRecord(value);
  if (!record) return undefined;
  return {
    x: clamp(asFiniteNumber(record.x), -50, 50),
    y: clamp(asFiniteNumber(record.y), -50, 50),
    blur: clamp(asFiniteNumber(record.blur), 0, 100),
    spread: clamp(asFiniteNumber(record.spread), -20, 50),
    color: typeof record.color === 'string' ? record.color : undefined,
  };
}

function normalizeTextShadow(value: unknown): ButtonTextShadowValue | undefined {
  const record = asRecord(value);
  if (!record) return undefined;
  return {
    x: clamp(asFiniteNumber(record.x), -20, 20),
    y: clamp(asFiniteNumber(record.y), -20, 20),
    blur: clamp(asFiniteNumber(record.blur), 0, 40),
    color: typeof record.color === 'string' ? record.color : undefined,
  };
}

function normalizeTextGlow(value: unknown): ButtonTextGlowValue | undefined {
  const record = asRecord(value);
  if (!record) return undefined;
  return {
    blur: clamp(asFiniteNumber(record.blur), 0, 60),
    color: typeof record.color === 'string' ? record.color : undefined,
    opacity: clamp(asFiniteNumber(record.opacity), 0, 1),
  };
}

function normalizeTextRelief(value: unknown): ButtonTextReliefValue | undefined {
  const record = asRecord(value);
  if (!record) return undefined;
  return {
    enabled: typeof record.enabled === 'boolean' ? record.enabled : undefined,
    highlightColor: typeof record.highlightColor === 'string' ? record.highlightColor : undefined,
    shadowColor: typeof record.shadowColor === 'string' ? record.shadowColor : undefined,
    distance: clamp(asFiniteNumber(record.distance), 0, 12),
    blur: clamp(asFiniteNumber(record.blur), 0, 20),
    opacity: clamp(asFiniteNumber(record.opacity), 0, 1),
  };
}

function normalizeTextEffectToggles(value: unknown): ButtonTextEffectToggles | undefined {
  const record = asRecord(value);
  if (!record) return undefined;
  return {
    shadow: typeof record.shadow === 'boolean' ? record.shadow : undefined,
    outerGlow: typeof record.outerGlow === 'boolean' ? record.outerGlow : undefined,
    innerGlow: typeof record.innerGlow === 'boolean' ? record.innerGlow : undefined,
    emboss: typeof record.emboss === 'boolean' ? record.emboss : undefined,
    bevel: typeof record.bevel === 'boolean' ? record.bevel : undefined,
    pressed: typeof record.pressed === 'boolean' ? record.pressed : undefined,
  };
}

export function normalizeButtonVisualState(value: unknown): ButtonVisualState {
  const record = asRecord(value);
  if (!record) return {};
  const legacyTextEffectPreset =
    record.textEffectPreset === 'emboss' ||
    record.textEffectPreset === 'bevel' ||
    record.textEffectPreset === 'pressed' ||
    record.textEffectPreset === 'none'
      ? record.textEffectPreset
      : undefined;
  const textEffects = normalizeTextEffectToggles(record.textEffects);
  const borderStyle =
    record.borderStyle === 'solid' ||
    record.borderStyle === 'dashed' ||
    record.borderStyle === 'dotted' ||
    record.borderStyle === 'none'
      ? record.borderStyle
      : undefined;
  return {
    backgroundColor: typeof record.backgroundColor === 'string' ? record.backgroundColor : undefined,
    backgroundOpacity: clamp(asFiniteNumber(record.backgroundOpacity), 0, 1),
    borderColor: typeof record.borderColor === 'string' ? record.borderColor : undefined,
    borderWidth: clamp(asFiniteNumber(record.borderWidth), 0, 10),
    borderStyle,
    borderRadius: clamp(asFiniteNumber(record.borderRadius), 0, 50),
    borderLinked: typeof record.borderLinked === 'boolean' ? record.borderLinked : undefined,
    borderSides: normalizeBorderSides(record.borderSides),
    boxShadow: normalizeShadow(record.boxShadow),
    textColor: typeof record.textColor === 'string' ? record.textColor : undefined,
    fontFamilyKey: typeof record.fontFamilyKey === 'string' && record.fontFamilyKey.trim().length > 0
      ? record.fontFamilyKey.trim()
      : undefined,
    fontSize: clamp(asFiniteNumber(record.fontSize), 8, 1000),
    fontSizeMobile: clamp(asFiniteNumber(record.fontSizeMobile), 8, 1000),
    fontSizeDesktop: clamp(asFiniteNumber(record.fontSizeDesktop), 8, 1000),
    fontWeight: clamp(asFiniteNumber(record.fontWeight), 100, 900),
    fontStyle: record.fontStyle === 'italic' ? 'italic' : record.fontStyle === 'normal' ? 'normal' : undefined,
    textDecorationLine:
      record.textDecorationLine === 'underline'
        ? 'underline'
        : record.textDecorationLine === 'none'
          ? 'none'
          : undefined,
    textTransform:
      record.textTransform === 'uppercase' ||
      record.textTransform === 'lowercase' ||
      record.textTransform === 'capitalize' ||
      record.textTransform === 'none'
        ? record.textTransform
        : undefined,
    textAlign:
      record.textAlign === 'left' ||
      record.textAlign === 'center' ||
      record.textAlign === 'right'
        ? record.textAlign
        : undefined,
    textRole:
      record.textRole === 'body' ||
      record.textRole === 'heading' ||
      record.textRole === 'wordmark'
        ? record.textRole
        : undefined,
    textSizingMode:
      record.textSizingMode === 'frame' || record.textSizingMode === 'scale'
        ? record.textSizingMode
        : undefined,
    lineHeight: clamp(asFiniteNumber(record.lineHeight), 1, 2.2),
    letterSpacing: clamp(asFiniteNumber(record.letterSpacing), -2, 12),
    wordSpacing: clamp(asFiniteNumber(record.wordSpacing), -8, 40),
    textOpacity: clamp(asFiniteNumber(record.textOpacity), 0, 1),
    textBlendMode:
      record.textBlendMode === 'multiply' ||
      record.textBlendMode === 'screen' ||
      record.textBlendMode === 'overlay' ||
      record.textBlendMode === 'soft-light' ||
      record.textBlendMode === 'difference' ||
      record.textBlendMode === 'exclusion' ||
      record.textBlendMode === 'normal'
        ? record.textBlendMode
        : undefined,
    textShadow: normalizeTextShadow(record.textShadow),
    textFillMode:
      record.textFillMode === 'solid' ||
      record.textFillMode === 'hollow' ||
      record.textFillMode === 'image' ||
      record.textFillMode === 'gradient'
        ? record.textFillMode
        : undefined,
    textFillImageUrl:
      typeof record.textFillImageUrl === 'string' && record.textFillImageUrl.trim().length > 0
        ? record.textFillImageUrl.trim()
        : undefined,
    textFillImageScale: clamp(asFiniteNumber(record.textFillImageScale), 10, 400),
    textFillImagePositionX: clamp(asFiniteNumber(record.textFillImagePositionX), -50, 50),
    textFillImagePositionY: clamp(asFiniteNumber(record.textFillImagePositionY), -50, 50),
    textGradientStartColor:
      typeof record.textGradientStartColor === 'string' ? record.textGradientStartColor : undefined,
    textGradientEndColor:
      typeof record.textGradientEndColor === 'string' ? record.textGradientEndColor : undefined,
    textGradientAngle: clamp(asFiniteNumber(record.textGradientAngle), 0, 360),
    textStrokeColor: typeof record.textStrokeColor === 'string' ? record.textStrokeColor : undefined,
    textStrokeWidth: clamp(asFiniteNumber(record.textStrokeWidth), 0, 8),
    textOuterGlow: normalizeTextGlow(record.textOuterGlow),
    textInnerGlow: normalizeTextGlow(record.textInnerGlow),
    textEffects: {
      ...textEffects,
      emboss:
        textEffects?.emboss ??
        (legacyTextEffectPreset === 'emboss' ? true : undefined),
      bevel:
        textEffects?.bevel ??
        (legacyTextEffectPreset === 'bevel' ? true : undefined),
      pressed:
        textEffects?.pressed ??
        (legacyTextEffectPreset === 'pressed' ? true : undefined),
    },
    textEmboss: normalizeTextRelief(record.textEmboss),
    textBevel: normalizeTextRelief(record.textBevel),
    textPressed: normalizeTextRelief(record.textPressed),
    textEffectPreset: legacyTextEffectPreset,
    imageFit:
      record.imageFit === 'cover' || record.imageFit === 'contain' || record.imageFit === 'fill'
        ? record.imageFit
        : undefined,
    imageScale: clamp(asFiniteNumber(record.imageScale), 10, 300),
    imageScaleX: clamp(asFiniteNumber(record.imageScaleX), 0.1, 5),
    imageScaleY: clamp(asFiniteNumber(record.imageScaleY), 0.1, 5),
    imageRotation: clamp(asFiniteNumber(record.imageRotation), -3600, 3600),
    imagePositionX: clamp(asFiniteNumber(record.imagePositionX), -100, 100),
    imagePositionY: clamp(asFiniteNumber(record.imagePositionY), -100, 100),
    imageFlipX: typeof record.imageFlipX === 'boolean' ? record.imageFlipX : undefined,
    imageFlipY: typeof record.imageFlipY === 'boolean' ? record.imageFlipY : undefined,
    sizingMode: normalizeButtonSizingMode(record.sizingMode),
    visualHeight: normalizeButtonVisualHeight(record.visualHeight),
    alignX: normalizeButtonAlignX(record.alignX),
    alignY: normalizeButtonAlignY(record.alignY),
    paddingX: clamp(asFiniteNumber(record.paddingX), 0, 64),
    paddingY: clamp(asFiniteNumber(record.paddingY), 0, 40),
    marginX: clamp(asFiniteNumber(record.marginX), 0, 64),
    marginY: clamp(asFiniteNumber(record.marginY), 0, 40),
  };
}

export function normalizeButtonDesignStyle(style: unknown): ButtonDesignStyle {
  const record = asRecord(style);
  const base = normalizeButtonVisualState(record);
  return {
    ...base,
    hover: normalizeButtonVisualState(record?.hover),
  };
}

function hexToRgba(color: string, opacity?: number) {
  if (!color) return undefined;
  if (opacity === undefined) return color;
  const normalized = color.trim();
  const hex = normalized.startsWith('#') ? normalized.slice(1) : normalized;
  const validHex = /^[0-9a-f]{3}([0-9a-f]{3})?$/i.test(hex);
  if (!validHex) return normalized;
  const expanded = hex.length === 3 ? hex.split('').map((part) => `${part}${part}`).join('') : hex;
  const r = parseInt(expanded.slice(0, 2), 16);
  const g = parseInt(expanded.slice(2, 4), 16);
  const b = parseInt(expanded.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(1, opacity))})`;
}

function shadowToCss(shadow?: ButtonShadowValue) {
  if (!shadow) return undefined;
  const x = shadow.x ?? 0;
  const y = shadow.y ?? 0;
  const blur = shadow.blur ?? 0;
  const spread = shadow.spread ?? 0;
  const color = shadow.color ?? 'rgba(15, 23, 42, 0.18)';
  if (x === 0 && y === 0 && blur === 0 && spread === 0 && !shadow.color) return undefined;
  return `${x}px ${y}px ${blur}px ${spread}px ${color}`;
}

function textShadowToCss(shadow?: ButtonTextShadowValue) {
  if (!shadow) return undefined;
  const x = shadow.x ?? 0;
  const y = shadow.y ?? 0;
  const blur = shadow.blur ?? 0;
  const color = shadow.color ?? 'rgba(15, 23, 42, 0.3)';
  if (x === 0 && y === 0 && blur === 0 && !shadow.color) return undefined;
  return `${x}px ${y}px ${blur}px ${color}`;
}

function withAlpha(color: string | undefined, fallback: string, opacity: number) {
  return hexToRgba(color ?? fallback, opacity) ?? fallback;
}

function buildReliefLayers(
  config: ButtonTextReliefValue | undefined,
  defaults: {
    highlightColor: string;
    shadowColor: string;
    distance: number;
    blur: number;
    opacity: number;
    pressed?: boolean;
    extraShadow?: string;
  }
) {
  if (!config?.enabled) return [];
  const distance = config.distance ?? defaults.distance;
  const blur = config.blur ?? defaults.blur;
  const opacity = config.opacity ?? defaults.opacity;
  const highlightColor = withAlpha(config.highlightColor, defaults.highlightColor, opacity);
  const shadowColor = withAlpha(config.shadowColor, defaults.shadowColor, opacity);
  const sign = defaults.pressed ? -1 : 1;
  const highlightX = -distance * sign;
  const highlightY = -distance * sign;
  const shadowX = distance * sign;
  const shadowY = distance * sign;
  const layers = [
    `${highlightX}px ${highlightY}px ${blur}px ${highlightColor}`,
    `${shadowX}px ${shadowY}px ${blur}px ${shadowColor}`,
  ];
  if (defaults.extraShadow) {
    layers.push(defaults.extraShadow);
  }
  return layers;
}

function buildTextEffectLayers(state?: ButtonVisualState) {
  if (!state) return undefined;
  const layers: string[] = [];
  const effectToggles = state.textEffects ?? {};
  const shadowEnabled = effectToggles.shadow ?? !!textShadowToCss(state.textShadow);
  const outerGlowEnabled = effectToggles.outerGlow ?? !!state.textOuterGlow?.blur;
  const innerGlowEnabled = effectToggles.innerGlow ?? !!state.textInnerGlow?.blur;
  const embossEnabled = effectToggles.emboss ?? state.textEffectPreset === 'emboss';
  const bevelEnabled = effectToggles.bevel ?? state.textEffectPreset === 'bevel';
  const pressedEnabled = effectToggles.pressed ?? state.textEffectPreset === 'pressed';

  const baseShadow = shadowEnabled ? textShadowToCss(state.textShadow) : undefined;
  if (baseShadow) {
    layers.push(baseShadow);
  }

  const outerGlow = state.textOuterGlow;
  if (outerGlowEnabled && outerGlow?.blur && outerGlow.blur > 0) {
    const outerColor = withAlpha(outerGlow.color, '#60a5fa', outerGlow.opacity ?? 0.7);
    layers.push(`0 0 ${outerGlow.blur}px ${outerColor}`);
    layers.push(`0 0 ${Math.max(1, Math.round(outerGlow.blur * 0.45))}px ${outerColor}`);
  }

  const innerGlow = state.textInnerGlow;
  if (innerGlowEnabled && innerGlow?.blur && innerGlow.blur > 0) {
    const innerColor = withAlpha(innerGlow.color, '#ffffff', innerGlow.opacity ?? 0.38);
    const softInset = Math.max(1, Math.round(innerGlow.blur / 4));
    layers.push(`0 0 ${softInset}px ${innerColor}`);
    layers.push(`0 1px ${Math.max(1, Math.round(innerGlow.blur / 2))}px ${innerColor}`);
  }

  if (embossEnabled) {
    layers.push(
      ...buildReliefLayers(
        {
          enabled: true,
          ...(state.textEmboss ?? {}),
        },
        {
          highlightColor: '#ffffff',
          shadowColor: '#0f172a',
          distance: 1,
          blur: 0,
          opacity: 0.45,
        }
      )
    );
  }

  if (bevelEnabled) {
    layers.push(
      ...buildReliefLayers(
        {
          enabled: true,
          ...(state.textBevel ?? {}),
        },
        {
          highlightColor: '#ffffff',
          shadowColor: '#0f172a',
          distance: 1,
          blur: 1,
          opacity: 0.5,
          extraShadow: '0 2px 4px rgba(15,23,42,0.12)',
        }
      )
    );
  }

  if (pressedEnabled) {
    layers.push(
      ...buildReliefLayers(
        {
          enabled: true,
          ...(state.textPressed ?? {}),
        },
        {
          highlightColor: '#ffffff',
          shadowColor: '#0f172a',
          distance: 1,
          blur: 1,
          opacity: 0.38,
          pressed: true,
        }
      )
    );
  }

  return layers.length > 0 ? layers.join(', ') : undefined;
}

export function resolveButtonFontSizeForViewport(
  state: ButtonVisualState | undefined,
  viewport?: 'desktop' | 'tablet' | 'mobile'
): number | undefined {
  if (!state || !viewport) return undefined;
  const fontSizeDesktop = state.fontSizeDesktop ?? state.fontSize;
  const fontSizeMobile = state.fontSizeMobile ?? fontSizeDesktop;
  if (viewport === 'mobile' || viewport === 'tablet') {
    return fontSizeMobile ?? fontSizeDesktop ?? state.fontSize;
  }
  return fontSizeDesktop ?? state.fontSize;
}

export function buttonVisualStateToReactStyle(
  state: ButtonVisualState | undefined,
  fontLibrary?: Record<string, string>,
  options?: { previewViewport?: 'desktop' | 'tablet' | 'mobile' }
): React.CSSProperties {
  if (!state) return {};
  const sizingMode = state.sizingMode ?? 'stretch';
  const visualHeight = normalizeButtonVisualHeight(state.visualHeight);
  const textFillMode = state.textFillMode ?? 'solid';
  const strokeWidth = state.textStrokeWidth ?? 0;
  const strokeColor = state.textStrokeColor ?? state.textColor;
  const fontSizeDesktop = state.fontSizeDesktop ?? state.fontSize;
  const fontSizeMobile = state.fontSizeMobile ?? fontSizeDesktop;
  const previewFontSize = resolveButtonFontSizeForViewport(state, options?.previewViewport);
  const responsiveFontMin =
    fontSizeDesktop !== undefined && fontSizeMobile !== undefined
      ? Math.min(fontSizeMobile, fontSizeDesktop)
      : undefined;
  const responsiveFontMax =
    fontSizeDesktop !== undefined && fontSizeMobile !== undefined
      ? Math.max(fontSizeMobile, fontSizeDesktop)
      : undefined;
  const baseStyle: React.CSSProperties = {
    backgroundColor: state.backgroundColor
      ? hexToRgba(state.backgroundColor, state.backgroundOpacity)
      : undefined,
    borderColor: state.borderColor,
    borderWidth: state.borderWidth,
    borderStyle: state.borderStyle,
    borderRadius: state.borderRadius,
    boxShadow: shadowToCss(state.boxShadow),
    color: state.textColor,
    fontFamily:
      state.fontFamilyKey && fontLibrary?.[state.fontFamilyKey]
        ? fontLibrary[state.fontFamilyKey]
        : undefined,
    fontSize:
      previewFontSize !== undefined
        ? `${previewFontSize}px`
        : fontSizeDesktop !== undefined &&
            fontSizeMobile !== undefined &&
            responsiveFontMin !== undefined &&
            responsiveFontMax !== undefined
          ? `clamp(${responsiveFontMin}px, calc(${fontSizeMobile}px + ${
              fontSizeDesktop - fontSizeMobile
            } * ((100vw - 375px) / 905)), ${responsiveFontMax}px)`
          : fontSizeDesktop,
    fontWeight: state.fontWeight,
    fontStyle: state.fontStyle,
    textDecorationLine: state.textDecorationLine,
    textTransform: state.textTransform,
    textAlign: state.textAlign,
    lineHeight: state.lineHeight,
    letterSpacing:
      state.letterSpacing !== undefined ? `${state.letterSpacing}px` : undefined,
    wordSpacing:
      state.wordSpacing !== undefined ? `${state.wordSpacing}px` : undefined,
    opacity: state.textOpacity,
    mixBlendMode: state.textBlendMode && state.textBlendMode !== 'normal' ? state.textBlendMode : undefined,
    textShadow: buildTextEffectLayers(state),
    paddingLeft: state.paddingX,
    paddingRight: state.paddingX,
    paddingTop: state.paddingY,
    paddingBottom: state.paddingY,
    marginLeft: state.marginX,
    marginRight: state.marginX,
    marginTop: state.marginY,
    marginBottom: state.marginY,
    width: sizingMode === 'hug' ? 'fit-content' : undefined,
    height: visualHeight,
    minHeight: visualHeight ?? (sizingMode === 'hug' ? 0 : undefined),
    maxHeight: visualHeight,
    maxWidth: '100%',
    WebkitTextStrokeWidth:
      strokeWidth > 0 ? `${strokeWidth}px` : undefined,
    WebkitTextStrokeColor:
      strokeWidth > 0 && strokeColor ? strokeColor : undefined,
  };

  if (textFillMode === 'hollow') {
    baseStyle.color = 'transparent';
    baseStyle.WebkitTextFillColor = 'transparent';
  }

  if (textFillMode === 'image' && state.textFillImageUrl) {
    baseStyle.color = 'transparent';
    baseStyle.WebkitTextFillColor = 'transparent';
    baseStyle.backgroundImage = `url("${state.textFillImageUrl.replace(/"/g, '\\"')}")`;
    baseStyle.backgroundSize = `${state.textFillImageScale ?? 100}% auto`;
    baseStyle.backgroundPosition = `${50 + (state.textFillImagePositionX ?? 0)}% ${50 +
      (state.textFillImagePositionY ?? 0)}%`;
    baseStyle.backgroundRepeat = 'no-repeat';
    baseStyle.backgroundClip = 'text';
    baseStyle.WebkitBackgroundClip = 'text';
  }

  if (textFillMode === 'gradient') {
    const startColor = state.textGradientStartColor ?? state.textColor ?? '#ffffff';
    const endColor = state.textGradientEndColor ?? '#60a5fa';
    const angle = state.textGradientAngle ?? 135;
    baseStyle.color = 'transparent';
    baseStyle.WebkitTextFillColor = 'transparent';
    baseStyle.backgroundImage = `linear-gradient(${angle}deg, ${startColor}, ${endColor})`;
    baseStyle.backgroundClip = 'text';
    baseStyle.WebkitBackgroundClip = 'text';
  }

  if (state.borderLinked === false && state.borderSides) {
    const top = state.borderSides.top;
    const right = state.borderSides.right;
    const bottom = state.borderSides.bottom;
    const left = state.borderSides.left;
    baseStyle.borderTopWidth = top?.width;
    baseStyle.borderTopColor = top?.color;
    baseStyle.borderTopStyle = top?.style;
    baseStyle.borderRightWidth = right?.width;
    baseStyle.borderRightColor = right?.color;
    baseStyle.borderRightStyle = right?.style;
    baseStyle.borderBottomWidth = bottom?.width;
    baseStyle.borderBottomColor = bottom?.color;
    baseStyle.borderBottomStyle = bottom?.style;
    baseStyle.borderLeftWidth = left?.width;
    baseStyle.borderLeftColor = left?.color;
    baseStyle.borderLeftStyle = left?.style;
  }

  return baseStyle;
}

export function buttonVisualStateToCssDeclarations(
  state: ButtonVisualState | undefined,
  fontLibrary?: Record<string, string>
): string {
  const style = buttonVisualStateToReactStyle(state, fontLibrary);
  return Object.entries(style)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => {
      const cssKey = key.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
      const rawValue =
        typeof value === 'number' &&
        key !== 'fontWeight' &&
        key !== 'lineHeight'
          ? `${value}px`
          : value;
      return `${cssKey}: ${rawValue};`;
    })
    .join(' ');
}
